// ============================================================================
// TERMO RENUNCIA ADVOGADO DATA — Termo de Renúncia ao Mandato
// Instrumento pelo qual o advogado/escritório renuncia ao mandato outorgado
// pelo cliente. Diferente do Termo de Revogação (ato do cliente) — ver
// TermoRevogacaoClienteData.js. Documentos separados por decisão do titular
// (obrigação estruturalmente distinta: aqui há dever de continuar
// respondendo por 10 dias, que a revogação não tem). Ver CONSTITUICAO_
// REGRAS.SH, artigos sobre a família de agentes de revisão jurídica
// (Art. 369-373) e o plano de implementação aprovado em 15/08/2026.
//
// ENXUTO DE PROPÓSITO (27/08/2026, Art. 389 do CONSTITUICAO_REGRAS.SH):
// contém apenas qualificação das partes (Cláusula 1ª), objeto/escopo do
// mandato renunciado (Cláusula 2ª, Art. 390 — mesmo padrão "[[OBJETO]]" da
// Procuração), o ato de renúncia (Cláusula 3ª), notificação ao mandante
// (Cláusula 4ª), prazo de 10 dias (Cláusula 5ª) e data/efeitos (Cláusula
// 6ª). Cláusulas "Dos Honorários já Vencidos" e "Da Entrega de Documentos
// e Prestação de Contas" foram REMOVIDAS — mesma pesquisa e mesma decisão
// do titular documentadas em TermoRevogacaoClienteData.js (topo do
// arquivo): essas cláusulas pertencem ao Contrato de Prestação de
// Serviços Advocatícios, não a este instrumento.
//
// Base legal (pesquisada e verificada em fonte oficial/quase-oficial em
// 15/08/2026 — ver histórico de sessão):
//   art. 133 da Constituição Federal de 1988
//   art. 112, caput e § 1º, da Lei n. 13.105/2015 (Código de Processo Civil)
//   art. 5º, § 3º, da Lei n. 8.906/1994 (Estatuto da Advocacia e da OAB)
//   arts. 15 e 16 do Código de Ética e Disciplina da OAB (Resolução n. 02/2015 CFOAB)
//
// GÊNERO DO TERMO INSTITUCIONAL ("a parte RENUNCIANTE"/"MANDANTE") — FIXO,
// NÃO VARIÁVEL, mesmo com múltiplos MANDANTES (ver Art. 391 abaixo). A
// concordância verbal/nominal do resto do documento continua
// singular-feminino invariável independentemente de quantos clientes
// reais estejam por trás de "a parte MANDANTE" — só o RÓTULO ("PARTE
// MANDANTE" vs. "PARTES MANDANTES", `{{mandanteLabel}}`) e a lista de
// qualificações (`{{mandanteQual}}`) mudam com a quantidade. Ver
// CONSTITUICAO_REGRAS.SH Art. 381 para a análise completa.
//
// MULTI-PARTE (27/08/2026, Art. 391 do CONSTITUICAO_REGRAS.SH): `clientsData`
// é um ARRAY de clientes (era `clientData`, objeto único, até esta data) —
// mesmo padrão de junção gramatical (`joinPartiesPt`) copiado do Contrato,
// não importado cruzado (Art. 350). A parte RENUNCIANTE (escritório)
// continua sempre singular, não precisou do mesmo tratamento.
// ============================================================================

import { buildCanonicalClientQualification } from "../../config/ClientSchema";
import { escapeHtml } from "../../utils/escapeHtml";

export const TERMO_RENUNCIA_STRUCTURE = [
  {
    id: "renuncia_preambulo",
    title: "PREÂMBULO E DIRETRIZES DE INTERPRETAÇÃO",
    type: "text",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-gavel",
    content:
      "<p>Considerando que a advocacia é função essencial à administração da justiça, nos termos do art. 133 da Constituição Federal de 1988;</p><p>Considerando que o advogado pode renunciar ao mandato a qualquer tempo, mediante notificação ao mandante, nos termos do art. 112, <em>caput</em> e § 1º, da Lei n. 13.105/2015 (Código de Processo Civil), e do art. 5º, § 3º, da Lei n. 8.906/1994 (Estatuto da Advocacia e da OAB);</p><p>Considerando o dever de não abandonar a causa sob seu patrocínio e a recomendação de renunciar diante de dificuldade insuperável ou quebra de confiança, sem obrigação de declarar o motivo, nos termos dos arts. 15 e 16 do Código de Ética e Disciplina da OAB (Resolução n. 02/2015 do Conselho Federal da OAB);</p><p>As partes qualificadas na <strong>Cláusula 1ª</strong> firmam o presente Termo de Renúncia ao Mandato.</p><p>Para fins de padronização e fluidez de leitura, este instrumento adotará a expressão institucional \"a parte <strong>RENUNCIANTE</strong>\" para referir-se ao advogado ou sociedade de advocacia que renuncia ao mandato, e \"a parte <strong>MANDANTE</strong>\" para referir-se ao cliente outorgante do mandato ora renunciado — designações genéricas e invariáveis, independentemente do gênero ou natureza das partes. Toda a concordância verbal e nominal das cláusulas a seguir obedecerá estritamente a esta regra (singular e feminino).</p><p>Ressalva-se que a padronização de gênero gramatical aqui adotada tem finalidade exclusivamente técnica de uniformização redacional, não refletindo qualquer declaração sobre o gênero ou natureza das partes, assegurando-se a validade do instrumento independentemente de eventuais usos gramaticais divergentes da concordância tradicional.</p>",
  },
  {
    id: "renuncia_cl01_partes",
    title: "Das Partes",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-user",
    children: [
      {
        id: "renuncia_cl01_item1",
        type: "sub_clause",
        content:
          "<strong>PARTE RENUNCIANTE:</strong> {{advQual}}",
        condition: "ALWAYS",
        level: 0,
      },
      {
        id: "renuncia_cl01_item2",
        type: "sub_clause",
        content: "<strong>{{mandanteLabel}}:</strong> {{mandanteQual}}",
        condition: "ALWAYS",
        level: 0,
      },
    ],
  },
  {
    id: "renuncia_cl02_objeto",
    title: "Do Objeto do Mandato Renunciado",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-scroll",
    children: [
      {
        id: "renuncia_cl02_item1",
        type: "sub_clause",
        content:
          "O mandato ora renunciado foi outorgado pela parte <strong>MANDANTE</strong> à parte <strong>RENUNCIANTE</strong> para a representação técnica e defesa de seus interesses atrelados ao seguinte escopo: <strong>{{objetoMandato}}</strong>, referente a {{processoRef}}.",
        condition: "ALWAYS",
        level: 0,
      },
    ],
  },
  {
    id: "renuncia_cl03_ato",
    title: "Do Ato de Renúncia",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-file-signature",
    children: [
      {
        id: "renuncia_cl03_item1",
        type: "sub_clause",
        content:
          "A parte <strong>RENUNCIANTE</strong> renuncia, nesta data, ao mandato descrito na <strong>Cláusula 2ª</strong>, que lhe foi outorgado pela parte <strong>MANDANTE</strong>, nos termos do art. 112 da Lei n. 13.105/2015 (Código de Processo Civil), não havendo obrigação de declarar o motivo da renúncia, nos termos do art. 16 do Código de Ética e Disciplina da OAB (Resolução n. 02/2015 do Conselho Federal da OAB), ressalvado o dever de sigilo profissional.",
        condition: "ALWAYS",
        level: 0,
      },
    ],
  },
  {
    id: "renuncia_cl03_notificacao",
    title: "Da Notificação ao Mandante",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-bell",
    children: [
      {
        id: "renuncia_cl03_item1",
        type: "sub_clause",
        content:
          "A parte <strong>MANDANTE</strong> fica, por este instrumento, formal e inequivocamente notificada da presente renúncia, para que, no prazo que entender necessário, constitua novo patrono de sua confiança, nos termos do art. 112, <em>caput</em>, da Lei n. 13.105/2015 (Código de Processo Civil).",
        condition: "ALWAYS",
        level: 0,
      },
    ],
  },
  {
    id: "renuncia_cl04_prazo10dias",
    title: "Do Prazo de 10 Dias e da Continuidade dos Atos Urgentes",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-hourglass-half",
    children: [
      {
        id: "renuncia_cl04_item1",
        type: "sub_clause",
        content:
          "Nos termos do art. 112, § 1º, da Lei n. 13.105/2015 (Código de Processo Civil), e do art. 5º, § 3º, da Lei n. 8.906/1994 (Estatuto da Advocacia e da OAB), a parte <strong>RENUNCIANTE</strong> continuará a representar a parte <strong>MANDANTE</strong> pelos <strong>10 (dez) dias</strong> seguintes à data desta notificação, exclusivamente na prática dos atos considerados urgentes e necessários para evitar-lhe prejuízo, salvo se, antes de expirado esse prazo, for constituído novo procurador nos autos.",
        condition: "ALWAYS",
        level: 0,
      },
    ],
  },
  {
    id: "renuncia_cl07_efeitos",
    title: "Da Data de Efeitos",
    type: "clause",
    tags: ["Universal"],
    condition: "ALWAYS",
    icon: "fa-calendar-check",
    children: [
      {
        id: "renuncia_cl07_item1",
        type: "sub_clause",
        content:
          "A presente renúncia produz efeitos a partir da data da notificação de que trata a <strong>Cláusula 4ª</strong> deste instrumento, observado o prazo de sobrevida de que trata a <strong>Cláusula 5ª</strong>.",
        condition: "ALWAYS",
        level: 0,
      },
    ],
  },
];

// ── Helpers para placeholders ────────────────────────────────────────────────
// Junta N qualificações em português com a mesma regra do Contrato
// (ContractEngine.jsx, joinPartiesPt): 1 item → o próprio; 2 itens → "A e
// B"; 3+ → "A; B; e C". Copiado, não importado cruzado (Art. 350).
const joinPartiesPt = (parts) => {
  const arr = (Array.isArray(parts) ? parts : []).filter(
    (x) => String(x || "").trim() !== "",
  );
  if (arr.length <= 1) return arr[0] || "";
  if (arr.length === 2) return `${arr[0]} e ${arr[1]}`;
  return `${arr.slice(0, -1).join("; ")}; e ${arr[arr.length - 1]}`;
};

const stripFinalPunct = (s) =>
  String(s || "").trim().replace(/[\s.,;]+$/g, "");

export function buildRenunciaPlaceholderVars({ clientsData, officeData, processoRef, objetoMandato }) {
  const clients = (Array.isArray(clientsData) ? clientsData : []).filter(Boolean);
  const isPlaceholder = clients.length === 0;

  // Qualificação canônica do escritório/advogado (mesma fonte que Contrato/
  // Procuração usam para CONTRATADA/OUTORGADA — Art. 383, CONSTITUICAO_
  // REGRAS.SH). isAttorneyOfRecord:true ativa "...e com escritório
  // profissional..." e a cláusula de citação/intimação no endereço
  // profissional, que é o texto correto aqui (o RENUNCIANTE é o próprio
  // advogado/escritório, não um cliente comum).
  let advQual = "...";
  if (officeData) {
    advQual = String(
      buildCanonicalClientQualification(officeData, {}, { isAttorneyOfRecord: true })?.text || "",
    ).trim();
  }

  // Qualificação canônica — mesma fonte que advQual acima e que Contrato/
  // Procuração usam (buildCanonicalClientQualification), agora suportando
  // N MANDANTES (Art. 391, CONSTITUICAO_REGRAS.SH — mesmo padrão da
  // Revogação, ver TermoRevogacaoClienteData.js para o caso real que
  // motivou). Cada qualificação individual já vem com ponto final;
  // removido antes de juntar (joinPartiesPt) e reinserido uma única vez no
  // final do texto combinado. "condição de saúde declarada" é removida por
  // ser dado administrativo interno, antes do join.
  let mandanteQual = "...";
  let mandanteLabel = "PARTE MANDANTE";
  if (!isPlaceholder) {
    const rawParts = clients.map((c) => {
      const raw = String(
        buildCanonicalClientQualification(c, {})?.text || "",
      ).trim();
      const cleaned = raw.replace(
        /,?\s*condição de saúde declarada:\s*[^,.]+/gi,
        "",
      );
      return stripFinalPunct(cleaned);
    });
    const joined = joinPartiesPt(rawParts);
    mandanteQual = joined ? `${joined}.` : "...";
    mandanteLabel = clients.length > 1 ? "PARTES MANDANTES" : "PARTE MANDANTE";
  }

  return {
    "{{advQual}}": advQual,
    "{{mandanteQual}}": mandanteQual,
    "{{mandanteLabel}}": mandanteLabel,
    "{{processoRef}}": String(processoRef || "[processo/causa a especificar]").trim(),
    "{{objetoMandato}}": String(objetoMandato || "[objeto do mandato a especificar]").trim(),
  };
}

// Chaves cujo valor já é HTML pré-montado e seguro (mandanteQual/advQual
// escapam o texto livre na origem via ClientSchema.js antes de embrulhar em
// HTML) — mesmo padrão de proteção contra XSS já aplicado em Declaração/
// Procuração/Contrato (Art. 341/343).
const HTML_TRUSTED_VARS = new Set(["{{mandanteQual}}", "{{advQual}}"]);

export function resolveRenunciaText(text, vars) {
  let result = String(text || "");
  for (const [key, val] of Object.entries(vars)) {
    const safeVal = HTML_TRUSTED_VARS.has(key) ? String(val ?? "") : escapeHtml(val);
    result = result.split(key).join(safeVal);
  }
  return result;
}

// Todos os blocos deste documento são ALWAYS — sem condições variáveis (ao
// contrário da Declaração, que tem motivos A-F). Mantido como função para
// espelhar a assinatura da Declaração/Procuração e permitir extensão futura
// sem quebrar chamadas existentes.
export function isRenunciaBlockVisible() {
  return true;
}
export function isRenunciaChildVisible() {
  return true;
}
